import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApplication, useReplaceCv, useViewCv } from '../../api/applications';
import FeedbackPanel from '../../components/FeedbackPanel';
import InterviewerManager from '../../components/InterviewerManager';
import ReviewPanel from '../../components/ReviewPanel';
import StatusBadge from '../../components/StatusBadge';

export default function ApplicationDetail() {
  const { id } = useParams();
  const { data: a, isLoading, error } = useApplication(id);
  const viewCv = useViewCv();
  const replaceCv = useReplaceCv(id!);
  const fileRef = useRef<HTMLInputElement>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">Could not load application: {String(error)}</p>;
  if (!a) return null;

  const openCv = async () => {
    setCvError(null); setOpening(true);
    try { await viewCv(a.id); } catch (e) { setCvError(e instanceof Error ? e.message : 'Could not open CV'); }
    finally { setOpening(false); }
  };

  const onReplace = (file: File | undefined) => {
    if (!file) return;
    setCvError(null);
    if (file.type !== 'application/pdf') return setCvError('CV must be a PDF');
    const form = new FormData();
    form.append('cv', file);
    replaceCv.mutate(form, { onError: (e) => setCvError(e instanceof Error ? e.message : 'Upload failed') });
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <Link to={`/candidates/${a.candidate.id}`} className="muted small">← {a.candidate.fullName}</Link>
          <h2 style={{ marginTop: '0.4rem' }}>{a.candidate.fullName}</h2>
        </div>
        <StatusBadge status={a.status} />
      </div>

      <div className="meta-grid">
        <div><span className="meta-label">Email</span><span>{a.candidate.email}</span></div>
        <div><span className="meta-label">Phone</span><span>{a.candidate.phone ?? '—'}</span></div>
        <div><span className="meta-label">Job</span><Link to={`/jobs/${a.job.id}`}>{a.job.title}</Link></div>
        <div><span className="meta-label">Proposed by</span><span>{a.proposedBy.displayName}</span></div>
        {a.canManage && (
          <div><span className="meta-label">Compensation expectations</span><span>{a.candidate.compensation ?? '—'}</span></div>
        )}
      </div>

      <h3>CV</h3>
      {a.cv ? (
        <div className="cv-row">
          <span className="muted">{a.cv.filename}</span>
          <button className="btn-primary" onClick={openCv} disabled={opening}>{opening ? 'Opening…' : 'View PDF'}</button>
          {a.canManage && (
            <>
              <button className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={replaceCv.isPending}>
                {replaceCv.isPending ? 'Uploading…' : 'Replace CV'}
              </button>
              <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                onChange={(e) => onReplace(e.target.files?.[0])} />
            </>
          )}
        </div>
      ) : <p className="muted">No CV on file.</p>}
      {cvError && <div className="banner-error">{cvError}</div>}

      <h3 style={{ marginTop: '1.75rem' }}>Review</h3>
      <ReviewPanel application={a} />

      <h3 style={{ marginTop: '1.75rem' }}>Interviewer</h3>
      {a.canManageAssignments ? (
        <InterviewerManager jobId={a.job.id} applicationId={a.id} interviewer={a.interviewer} />
      ) : a.interviewer ? <p>{a.interviewer.user.displayName}</p> : <p className="muted">No interviewer assigned yet.</p>}

      <h3 style={{ marginTop: '1.75rem' }}>Interview feedback</h3>
      <FeedbackPanel application={a} />
    </div>
  );
}
